/**
 * Qué app abrió la sesión. No es una etiqueta descriptiva: de este valor
 * dependen el nombre de la cookie, su `path` y el claim `app` del JWT.
 */
export type SessionAppId = "client" | "compliance";

/**
 * Cookie de sesión POR APP (TALAPP-1890).
 *
 * Antes las dos apps emitían la MISMA cookie (`app_session_id`, `path:"/"`) y,
 * como comparten `JWT_SECRET` y el claim `appId` ("ava-app"), en un mismo
 * navegador la sesión del ejecutivo y la del solicitante se pisaban entre sí.
 * Son dos fronteras independientes y las dos hacen falta:
 *
 *   - `path` es la frontera del NAVEGADOR. El browser no manda la cookie de
 *     compliance a `/`, así que la sesión del ejecutivo ni siquiera sale hacia
 *     ava-client. Esto es lo que evita que una app pise la cookie de la otra.
 *   - `name` es la frontera del SERVIDOR. Cada proceso lee SOLO la cookie de su
 *     app, así que aunque el navegador mandara las dos (mismo host, paths
 *     solapados en `/compliance`), ava-client no puede tomar por suya la del
 *     ejecutivo.
 *
 * ⚠️ El `path` de compliance TIENE que coincidir con tres cosas o la cookie
 * simplemente no viaja y el ejecutivo queda deslogueado en cada request:
 *   1. `API_BASE` del SPA de compliance (`apps/compliance/client/src/const.ts`),
 *   2. el `location /compliance/api/` de las 4 plantillas de nginx
 *      (`nginx/conf.d/default.conf` y `nginx/default.conf.{qa,preprod,prod}`),
 *   3. el prefijo con que se sirve la app de compliance.
 * Cambiar uno solo de los tres rompe el login de compliance en silencio.
 */
export const SESSION_COOKIE: Record<SessionAppId, { name: string; path: string }> = {
  client:     { name: "ava_client_session",     path: "/" },
  compliance: { name: "ava_compliance_session", path: "/compliance" },
};

export const ONE_YEAR_MS = 1000 * 60 * 60 * 24 * 365;
export const AXIOS_TIMEOUT_MS = 30_000;
export const UNAUTHED_ERR_MSG = 'Please login (10001)';
export const NOT_ADMIN_ERR_MSG = 'You do not have required permission (10002)';

/**
 * Sprint 4 — Rejection codes (TALAPP-1594)
 * Catalog of reasons a compliance reviewer or pipeline can use when
 * rejecting a document. Persisted in audit_logs.details and surfaced in UI.
 */
export const REJECTION_CODES = {
  WRONG_TYPE: "WRONG_TYPE",
  ILLEGIBLE: "ILLEGIBLE",
  EXPIRED: "EXPIRED",
  INCOMPLETE: "INCOMPLETE",
  MISMATCH_DATA: "MISMATCH_DATA",
  OTHER: "OTHER",
} as const;

export type RejectionCode = typeof REJECTION_CODES[keyof typeof REJECTION_CODES];
